import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { runes, COURSE_SOURCES, WIKIPEDIA_SOURCES } from '../data/runes.fi.js';
import './RunesCourseIndex.css';

const THEMES = [...new Set(runes.flatMap((r) => r.themesFi || r.teemaFi || []))].sort();

const RunesCourseIndex = () => {
  const [search, setSearch] = useState('');
  const [aettFilter, setAettFilter] = useState('');
  const [teemaFilter, setTeemaFilter] = useState('');
  const [openSection, setOpenSection] = useState(null);
  const teematRef = useRef(null);
  const searchInputRef = useRef(null);

  const filtered = useMemo(() => {
    const q = (search || '').trim().toLowerCase();
    return runes.filter((r) => {
      const matchSearch = !q || r.nameFi.toLowerCase().includes(q) ||
        (r.phonetic && r.phonetic.toLowerCase().includes(q)) ||
        (r.keywordsFi && r.keywordsFi.some((k) => k.toLowerCase().includes(q))) ||
        ((r.themesFi || r.teemaFi || []).some((t) => t.toLowerCase().includes(q)));
      const matchAett = !aettFilter || String(r.aett) === String(aettFilter);
      const themes = r.themesFi || r.teemaFi || [];
      const matchTeema = !teemaFilter || themes.includes(teemaFilter);
      return matchSearch && matchAett && matchTeema;
    });
  }, [search, aettFilter, teemaFilter]);

  const openSectionFromHash = () => {
    if (typeof window === 'undefined') return;
    const hash = window.location.hash;
    if (hash === '#johdanto') {
      setOpenSection(1);
      setTimeout(() => document.getElementById('johdanto')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
    } else if (hash === '#historia-ja-mytologia') {
      setOpenSection(5);
      setTimeout(() => document.getElementById('historia-ja-mytologia')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
    } else if (hash === '#miten-opiskella') {
      setOpenSection(6);
      setTimeout(() => document.getElementById('miten-opiskella')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
    } else if (hash === '#perusolemus' || hash === '#hakemisto' || hash === '#teemat') {
      setOpenSection(7);
      if (hash === '#teemat') {
        setTimeout(() => {
          teematRef.current?.scrollIntoView({ behavior: 'smooth' });
          searchInputRef.current?.focus();
        }, 300);
      } else {
        setTimeout(() => document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
      }
    } else if (hash === '#7-paivaa') {
      setOpenSection(8);
      setTimeout(() => document.getElementById('7-paivaa')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
    } else if (hash === '#24-paivaa') {
      setOpenSection(9);
      setTimeout(() => document.getElementById('24-paivaa')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
    }
  };

  useEffect(() => {
    openSectionFromHash();
    window.addEventListener('hashchange', openSectionFromHash);
    return () => window.removeEventListener('hashchange', openSectionFromHash);
  }, []);

  const scrollToTeematAndFocus = (e) => {
    e.preventDefault();
    setOpenSection(7);
    setTimeout(() => {
      teematRef.current?.scrollIntoView({ behavior: 'smooth' });
      searchInputRef.current?.focus();
    }, 100);
  };

  const openAndScrollTo24 = (e) => {
    if (e) e.preventDefault();
    setOpenSection(9);
    setTimeout(() => {
      document.getElementById('24-paivaa')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
    if (typeof window !== 'undefined') window.location.hash = '#24-paivaa';
  };

  const scrollToAloita = (e) => {
    if (e) e.preventDefault();
    document.getElementById('aloita')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="runes-course-index">
      <div className="container">
        <div className="runes-course-header">
          <h1>Riimukurssi</h1>
          <p className="runes-course-intro">
            Riimut ovat peili – ne eivät ennusta tulevaisuutta vaan auttavat
            näkemään sen, mitä jo tiedät ja tarvitset kuulla. Tutustu riimuihin
            yksi kerrallaan ja käyt niitä omana työkaluna.
          </p>

          <section id="aloita" className="runes-course-block runes-start-card card" aria-label="Aloita tästä">
            <h2 className="runes-start-card-title">ALOITA TÄSTÄ</h2>
            <p className="runes-start-card-intro">
              Selkeä oppimispolku: ymmärrys → konteksti → metodi → sisältö → harjoitus. Valitse mistä aloitat.
            </p>
            <div className="runes-start-card-links">
              <div className="runes-start-link-wrap">
                <a href="#johdanto" className="runes-start-link btn btn-primary">
                  Johdanto riimuihin
                </a>
                <span className="runes-start-link-helper">Riimut ovat symbolinen kieli ja tietoisuuden työkalu. Niitä käytetään itsetutkiskeluun, selkeyttämiseen, suojaukseen, päätöksenteon tueksi ja rituaaliseen työskentelyyn.</span>
              </div>
              <div className="runes-start-link-wrap">
                <a href="#historia-ja-mytologia" className="runes-start-link btn btn-secondary">
                  Riimujen historia ja mytologia
                </a>
                <span className="runes-start-link-helper">Riimujen alkuperä, mytologinen tausta ja niiden pyhä rooli pohjoisessa perinteessä.</span>
              </div>
              <div className="runes-start-link-wrap">
                <a href="#miten-opiskella" className="runes-start-link btn btn-secondary">
                  Miten riimuja luetaan ja opiskellaan
                </a>
                <span className="runes-start-link-helper">Miten riimujen opiskelu etenee aettien, teemojen ja intuition kautta. Ei ulkoa opettelua, vaan kokemuksellista työskentelyä.</span>
              </div>
              <div className="runes-start-link-wrap">
                <a href="#hakemisto" className="runes-start-link btn btn-secondary">
                  Riimujen perusolemus – symbolinen kartta
                </a>
                <span className="runes-start-link-helper">Kaikki riimut yhdessä paikassa – kokonaiskuva Elder Futharkista.</span>
              </div>
              <div className="runes-start-link-wrap">
                <a href="#aakkosjarjestys" className="runes-start-link btn btn-secondary">
                  Selaa riimuja aakkosjärjestyksessä
                </a>
                <span className="runes-start-link-helper">Looginen tapa selata riimuja ja käyttää hakemistoa.</span>
              </div>
              <div className="runes-start-link-wrap">
                <a href="#teemat" className="runes-start-link btn btn-secondary" onClick={scrollToTeematAndFocus}>
                  Selaa riimuja teeman mukaan
                </a>
                <span className="runes-start-link-helper">Suoja, muutos, kasvu, rajat, yhteys ja selkeys.</span>
              </div>
              <div className="runes-start-link-wrap">
                <a href="#7-paivaa" className="runes-start-link btn btn-secondary">
                  7 päivän riimuharjoitus
                </a>
                <span className="runes-start-link-helper">Yksi riimu päivässä, 5–10 minuuttia. Kevyt ja arkeen sopiva aloitus.</span>
              </div>
              <div className="runes-start-link-wrap">
                <a href="#24-paivaa" className="runes-start-link btn btn-secondary" onClick={openAndScrollTo24}>
                  24 päivän syventävä riimupolku
                </a>
                <span className="runes-start-link-helper">Koko Elder Futhark, yksi riimu päivässä. Syvällinen ja eheä oppipolku.</span>
              </div>
            </div>
          </section>
        </div>

        <section className="runes-accordion-card card" id="johdanto">
          <button
            type="button"
            className="runes-accordion-trigger"
            aria-expanded={openSection === 1}
            onClick={() => setOpenSection(openSection === 1 ? null : 1)}
          >
            <span className="runes-accordion-title">Mitä riimut ovat</span>
          </button>
          {openSection === 1 && (
            <div className="runes-accordion-content">
              <p>
                Riimut ovat samaan aikaan symboleja, ääntä ja voimia. Jokainen riimu voi tarkoittaa kirjainta, äännettä tai perusolemusta – se riippuu kontekstista ja siitä, miten sitä käytät. Historiallisesti ne syntyivät pohjoisessa kirjoitus- ja merkintäperinteessä, mutta niiden voima nykypäivän työkaluna ei perustu ulkoa opeteltuun sanakirjaan vaan suhteeseen symbolin kanssa.
              </p>
              <p>
                Riimut eivät anna vastauksia – ne toimivat peilinä. Ne eivät kerro, mitä „pitää“ tapahtua, vaan auttavat näkemään sen, mitä jo tiedät tai mitä sisäinen äänesi tarvitsee kuulla. Tämä ero on keskeinen: kun lähestyt riimuja kysymyksenä „mitä tämä minulle tarkoittaa?“ et „mitä tämä objektiviisesti tarkoittaa?“, avautuu symbolinen lukutapa.
              </p>
              <p>
                Riimut tarjoavat tavan jäsentää merkitystä – ei lukuja tai ennustuksia, vaan teemoja: turvallisuus, raja, muutos, yhteys, valo. Ne ovat kieli, jolla voit keskustella itsesi kanssa. Opiskelu on siis vähemmän ulkoa opettelua ja enemmän tutustumista: mitä tämä viiva, tämä äänne, tämä kuva herättää minussa?
              </p>
              <p>
                Suhde symboliin kasvaa käytön ja hiljaisen pohdinnan kautta. Ei tarvitse tietää kaikkea etukäteen – riittää, että otat yhden riimun kerrallaan ja kysyt: mitä tämä minulle tänään peilaa?
              </p>
              <p className="runes-back-to-top">
                <a href="#aloita" onClick={scrollToAloita}>↑ Takaisin Aloita tästä</a>
              </p>
            </div>
          )}
        </section>

        <section className="runes-accordion-card card">
          <button
            type="button"
            className="runes-accordion-trigger"
            aria-expanded={openSection === 2}
            onClick={() => setOpenSection(openSection === 2 ? null : 2)}
          >
            <span className="runes-accordion-title">Riimut kirjoituksena ja arjessa</span>
          </button>
          {openSection === 2 && (
            <div className="runes-accordion-content">
              <p>
                Historiallisesti riimut olivat ensin kirjoitus: nimiä, viestejä, muistomerkkejä kynnyskiviin, aseisiin ja arki-esineisiin. Suorat viivat olivat käytännöllisiä – niitä oli helppo vuollaan puuhun, kiveen tai metalliin. Mutta merkintä ei ollut pelkkää tiedon siirtoa: se merkitsi rajaa, omistusta ja tarkoitusta. Joku oli jättänyt merkin – „tämä on minun“, „tämä on pyhitetty“, „muista tätä“.
              </p>
              <p>
                Symbolit muokkaavat tietoisuutta. Kun näet merkin, se vetää huomion tiettyyn ajatukseen tai tunteeseen. Sama periaate toimii nykyään: kun kirjaat päiväkirjaan yhden riimun tai lauseen, et vain tallenna tietoa – luot rajan. „Tässä pysähdyn. Tämä on tärkeää.“ Nimeäminen ja merkitseminen ovat tapoja tehdä valinnat näkyviksi.
              </p>
              <p>
                Arkielämässä riimut voivat ilmetä hiljaisina käytäntöinä: yksi riimu aamun alussa, yksi lause päiväkirjassa, yksi merkki kynnyskohdassa – ei rituaalina vaan tietoisena merkintänä. Mitä haluat merkitä? Mihin haluat asettaa rajan? Mitä nimeät tänään?
              </p>
              <p className="runes-back-to-top">
                <a href="#aloita" onClick={scrollToAloita}>↑ Takaisin Aloita tästä</a>
              </p>
            </div>
          )}
        </section>

        <section className="runes-accordion-card card">
          <button
            type="button"
            className="runes-accordion-trigger"
            aria-expanded={openSection === 3}
            onClick={() => setOpenSection(openSection === 3 ? null : 3)}
          >
            <span className="runes-accordion-title">Aikakaudet ja riimujärjestelmät (Elder / Younger / Futhark)</span>
          </button>
          {openSection === 3 && (
            <div className="runes-accordion-content">
              <p>
                Riimujärjestelmiä on useita, koska kieli, maantiede ja aika muuttuivat: äännejärjestelmät kevenivät tai laajenivat, kirjoitus käytännön tarpeisiin. Tämä ei tee yhdestä järjestelmästä parempaa – sama joki, eri rannat. Järjestelmät eivät ole hierarkia vaan evoluutio; jokainen palvelee omaa kontekstiaan.
              </p>

              <h4 className="runes-explain-subheading">Elder Futhark (kurssin perusta)</h4>
              <p>
                Vanha futhark (Elder Futhark) on varhaisin laajasti tunnettu riimujärjestelmä: noin 24 riimua, käytössä arviolta noin 100–800 jaa. Rakenne perustuu kolmeen aettiin (kahdeksan riimua per aetti), mikä antaa selkeän loogisen pohjan. Historiallisesti se oli kirjoitus- ja merkintäperinne; nykysymbolisessa käytössä monet alkavat tästä järjestelmästä.
              </p>
              <p>
                Tässä kurssissa keskitymme Elder Futhark -järjestelmään. Syy ei ole „maaginen ylivertaisuus“ – muita järjestelmiä ei pidetä heikompina. Valinta perustuu selkeyteen, symboliseen täydellisyyteen ja soveltuvuuteen intuitiiviseen ja reflektiiviseen työskentelyyn. Yksi eheä perusta helpottaa syvempää harjoittelua; laajentaminen muihin järjestelmiin voi tulla myöhemmin.
              </p>

              <h4 className="runes-explain-subheading">Younger Futhark</h4>
              <p>
                Nuorempi futhark (Younger Futhark) keveni noin 16 merkkiin ja oli käytössä skandinaavisella alueella viikinkiajalla. Äänteet ja symboliikka noudattavat omaa logiikkaansa: sama muoto voi vastata eri äännettä tai teemaa kuin vanhassa futharkissa. Merkitykset eivät siirry suoraan yhdestä järjestelmästä toiseen – nuorempi futhark vaatii oman tulkintakehikon.
              </p>
              <p>
                Tätä järjestelmää ei käytetä tässä kurssissa, mutta se kuuluu samaan pohjoiseen perinteeseen ja tunnustetaan osana riimujen historiaa. Jos opiskelet myöhemmin viikinkiaikaisia piirtokirjoituksia tai skandinaavista kontekstia, nuorempi futhark on oma aiheensa.
              </p>

              <h4 className="runes-explain-subheading">Muut perinteet ja nimitykset (Futhark / Futhorc)</h4>
              <p>
                Termi „Futhark“ toimii yleisenä kattoterminä näille pohjoisille riimujärjestelmille. Anglosaksisessa perinteessä käytetään nimitystä Futhorc (eri kirjoitusasu, lisämerkeillä); se oli käytössä Englannin tienoilla ja edustaa omaa alueellista kehitystä. Nämä perinteet ovat historiallisesti olemassa, mutta eivät ole tämän kurssin painopiste – mainitsemme ne kontekstin vuoksi.
              </p>

              <h4 className="runes-explain-subheading">Ohjausta nykylukijalle</h4>
              <p>
                Valitse harjoitteluun yksi järjestelmä ja syvenny siihen; muut jäävät taustatiedoksi. Tässä kurssissa työskentelemme Elder Futharkin kanssa – vakaa pohja, josta voit tarvittaessa myöhemmin laajentua. Merkitys syntyy käytöstä ja kontekstista, ei pelkistä listoista; yksi järjestelmä kerrallaan tukee sitä.
              </p>

              <p className="runes-back-to-top">
                <a href="#aloita" onClick={scrollToAloita}>↑ Takaisin Aloita tästä</a>
              </p>
            </div>
          )}
        </section>

        <section className="runes-accordion-card card">
          <button
            type="button"
            className="runes-accordion-trigger"
            aria-expanded={openSection === 4}
            onClick={() => setOpenSection(openSection === 4 ? null : 4)}
          >
            <span className="runes-accordion-title">Riimukivet, esineet ja arkiset merkinnät</span>
          </button>
          {openSection === 4 && (
            <div className="runes-accordion-content">
              <p>
                Historiallisesti riimut elivät maailmassa konkreettisina merkkeinä: kivissä, puussa, metallissa, koruissa. Riimukivet ja -patjat kertoivat muistosta ja omistuksesta; veitset, kammat ja astiat kantoivat lyhyitä viestejä tai omistajan nimeä. Merkintä oli kosketeltavaa – ei abstraktia teoriaa vaan toistoa, muistamista ja intentionaalisuutta. Joku oli käyttänyt aikaa siihen, että merkki jäi jäljen.
              </p>
              <p>
                Taktisuus ja toisto vahvistavat muistia. Kun piirrät riimun itse – paperille, kivelle, puulle – se ei ole vain visuaalinen muisti; keho osallistuu. Sama periaate toimii nykypäivänä: henkilökohtaiset esineet, piirrokset, hiljaiset merkintäkäytännöt. Ei tarvitse „oikeaa“ riimukiveä – riittää jotain, mikä on sinun ja mikä kantaa merkitystä.
              </p>
              <p>
                Arkiset merkinnät voivat olla yksityisiä: yksi riimu päiväkirjan reunalle, yksi lause työpöydälle, yksi symboli kynnyskohdassa. Tarkoitus ei ole näyttävyys vaan tietoinen suhde symboliin. Mitä haluat muistaa? Mitä haluat merkitä tänään?
              </p>
              <p className="runes-back-to-top">
                <a href="#aloita" onClick={scrollToAloita}>↑ Takaisin Aloita tästä</a>
              </p>
            </div>
          )}
        </section>

        <section className="runes-accordion-card card" id="historia-ja-mytologia">
          <button
            type="button"
            className="runes-accordion-trigger"
            aria-expanded={openSection === 5}
            onClick={() => setOpenSection(openSection === 5 ? null : 5)}
          >
            <span className="runes-accordion-title">Mytologia ja symbolinen lukutapa (nykylukijan työkaluna)</span>
          </button>
          {openSection === 5 && (
            <div className="runes-accordion-content">
              <p>
                Mytologia ei ole historianoppia eikä uskontoa – se on symbolinen kieli. Tarut eivät kerro, „miten asiat todella olivat“, vaan miten ihmiset ovat kuvanneet kokemuksia: pelkoa, toivoa, muutosta, uhrausta. Odinin riimujen „löytäminen“ – kuvaus, jossa jumala roikkuu puussa, uhraa itsensä ja saa näyn – on metafora. Se puhuu kärsivällisyydestä, antautumisesta ja muuttuneesta näkökulmasta. Ei tarvitse uskoa jumalaan kirjaimellisesti; riittää kysyä: mitä tämä kuva tekee minussa?
              </p>
              <p>
                Symbolinen lukutapa tarkoittaa: et kysy „mitä tämä tarkoittaa objektiivisesti?“ vaan „mitä tämä kuva, tämä tarina, tämä riimu herättää minussa?“ Vastaus on henkilökohtainen. Sama riimu voi yhdistyä eri teemoihin eri ihmisille; tärkeää on se, miten se resonoi sinun tilanteessasi.
              </p>
              <p>
                Mytologia toimii psykologisena ja henkisenä työkaluna kun etsit siitä elämää, ei faktoja. Odinin uhri voi tarkoittaa: jokin vanha minä „kuolee“, jotta uusi näkökulma syntyy. Riimujen „antaminen“ ihmisille voi tarkoittaa: tieto ja symbolit ovat jaettavissa – peili, ei salaisuus. Käytä myyttejä niin, että ne avaavat omaa sisäistä keskustelua, ei että ne määrittävät, mitä sinun „pitää“ uskoa.
              </p>
              <p>
                Tässä kurssissa riimut eivät ennusta tulevaisuutta – ne toimivat peilinä. Kysymys ei ole „mitä tapahtuu?“ vaan „mitä jo tiedän ja tarvitsen kuulla?“ Mytologia tukee tätä: se tarjoaa kuvia ja teemoja, joiden kautta voit lukea omaa elämääsi syvemmin.
              </p>
              <p className="runes-back-to-top">
                <a href="#aloita" onClick={scrollToAloita}>↑ Takaisin Aloita tästä</a>
              </p>
            </div>
          )}
        </section>

        <section className="runes-accordion-card card" id="miten-opiskella">
          <button
            type="button"
            className="runes-accordion-trigger"
            aria-expanded={openSection === 6}
            onClick={() => setOpenSection(openSection === 6 ? null : 6)}
          >
            <span className="runes-accordion-title">Miten opiskella: kolme kahdeksikkoa (aettir) + intuition peili</span>
          </button>
          {openSection === 6 && (
            <div className="runes-accordion-content">
              <p>
                Vanha futhark jaetaan kolmeen kahdeksan riimun ryhmään (aett). Opiskelu
                yhden aetin kerrallaan helpottaa: voit syventyä ensin yhteen ryhmään ja miettiä,
                mitä jokainen riimu sinulle peilaa. Käytä reflektiivista käytäntöä – päiväkirja,
                hiljaisuus, yksi riimu kerrallaan. Intuitio on peili: riimu ei kerro tulevaisuutta
                vaan auttaa näkemään sen, mitä jo tiedät.
              </p>

              <h3 className="runes-explain-heading">Mitä aetit ja teemat tarkoittavat?</h3>
              <p>
                Aetit ja teemat eivät ole sääntöjä vaan oppimisen ja tulkinnan apuvälineitä.
                Aetti kertoo vaiheen (perusta, haaste, integraatio); teema kertoo tilanteen tai
                kysymyksen (suoja, muutos, kasvu, rajat, yhteys, selkeys). Yhdessä ne auttavat
                valitsemaan ja lukemaan riimuja tarkoituksenmukaisesti – peilinä, ei ennustuksena.
              </p>

              <h4 className="runes-explain-subheading">Aetit – kolme kahdeksikon oppipolkua</h4>
              <p>
                Vanha futhark jaetaan kolmeen kahdeksan riimun ryhmään (aett 1, 2, 3). Aetit
                eivät ole jäykät säännöt vaan oppimisen vaiheita: voit ajatella niitä kolmena
                polkuna, joilla jokainen riimu saa kontekstinsa.
              </p>

              <h4 className="runes-explain-subheading">Aett 1 – Perusta</h4>
              <p>
                Ensimmäiset kahdeksan riimua: {runes.slice(0, 8).map((r) => r.nameFi).join(', ')}.
                Vaihe keskittyy perusvoimiin – turvallisuus, voima, rajat, viesti, liike, valo,
                antaminen, ilo. Tässä vaiheessa perustat yhteyden riimuihin ja tunnistat, mitä
                jokainen sinulle peilaa.
              </p>
              <p><strong>Esimerkkikysymyksiä:</strong></p>
              <ul className="runes-course-steps runes-explain-list">
                <li>Mitä minulla on jo – ja mitä olen valmis jakamaan?</li>
                <li>Missä tarvitsen rajaa tai selkeyttä?</li>
                <li>Mikä viesti tai opettaja toistuu elämässäni?</li>
              </ul>

              <h4 className="runes-explain-subheading">Aett 2 – Haaste ja muutos</h4>
              <p>
                Keskimmäinen kahdeksikko: {runes.slice(8, 16).map((r) => r.nameFi).join(', ')}.
                Vaihe käsittelee haasteita ja muutosta – luonnonvoimat, tarve, pysähtyminen,
                syklit, tuki, salattu mahdollisuus, suoja, valo. Tässä vaiheessa kohtaat sen,
                mitä vaatii hyväksymistä tai muutosta.
              </p>
              <p><strong>Esimerkkikysymyksiä:</strong></p>
              <ul className="runes-course-steps runes-explain-list">
                <li>Mitä en voi muuttaa – ja mitä voin hyväksyä?</li>
                <li>Mikä "sato" on kypsymässä ja tarvitsee aikaa?</li>
                <li>Missä tarvitsen turvaa tai rajaa?</li>
              </ul>

              <h4 className="runes-explain-subheading">Aett 3 – Integraatio ja selkeys</h4>
              <p>
                Viimeinen kahdeksan: {runes.slice(16, 24).map((r) => r.nameFi).join(', ')}.
                Vaihe yhdistää asiat – oikeus, kasvu, yhteistyö, identiteetti, vuo, potentiaali,
                läpimurto, koti. Tässä vaiheessa asiat selkeytyvät ja löydät oman paikkasi.
              </p>
              <p><strong>Esimerkkikysymyksiä:</strong></p>
              <ul className="runes-course-steps runes-explain-list">
                <li>Mitä pidän oikeana ja miten uskallan toimia?</li>
                <li>Mitä tai ketä hoidan – ja mitä haluan kasvattaa?</li>
                <li>Mistä tunnen kuuluvani?</li>
              </ul>

              <h4 className="runes-explain-subheading">Teemat – käytännön suodattimet tulkinnan tueksi</h4>
              <p>
                Teemat ovat tulkinnan apuvälineitä, ei ehtoja: ne auttavat kysymään „mikä tilanne
                tässä on?“ eivätkä määritä, mitä riimun „pitää“ tarkoittaa. Voit valita yhden
                teeman ja selata hakemistosta riimut, jotka resonoivat sen kanssa.
              </p>
              <ul className="runes-explain-list">
                <li><strong>Suoja</strong> – rajat, turvallisuus, vaisto. <em>Esimerkki: Missä tarvitsen rajaa tai turvaa?</em></li>
                <li><strong>Muutos</strong> – liike, luonnonvoimat, uusi alku. <em>Esimerkki: Mitä haluaa muuttua – ja mitä voin hyväksyä?</em></li>
                <li><strong>Kasvu</strong> – kypsyminen, hoito, potentiaali. <em>Esimerkki: Mitä minun pitäisi vaalia tai antaa kasvaa?</em></li>
                <li><strong>Rajat</strong> – selkeytys, kynnys, torjunta. <em>Esimerkki: Mihin minun pitäisi sanoa „ei“?</em></li>
                <li><strong>Yhteys</strong> – yhteistyö, kuuleminen, kuuluminen. <em>Esimerkki: Kenen tai minkä kanssa tunnet yhteyttä juuri nyt?</em></li>
                <li><strong>Selkeys</strong> – valo, oivallus, tarkoitus. <em>Esimerkki: Mikä asia tarvitsee valoa tai rehellistä katsantaa?</em></li>
              </ul>

              <h4 className="runes-explain-subheading">Yhdessä: aetti + teema</h4>
              <p>
                Teema antaa tilanteen tai kysymyksen („mikä aihe minua nyt liikuttaa?“), aetti
                antaa vaiheen („olenko perustamassa, kohdassa haasteessa vai integroimassa?“).
                Voit valita ensin teeman ja sitten aetin – tai päinvastoin – ja käyttää kurssin
                hakemistoa (Riimujen perusolemus) suodattimineen. Riimu pysyy peilinä: se ei
                ennusta vaan auttaa näkemään sen, mitä jo tiedät ja tarvitset kuulla.
              </p>

              <p className="runes-back-to-top">
                <a href="#aloita" onClick={scrollToAloita}>↑ Takaisin Aloita tästä</a>
              </p>
            </div>
          )}
        </section>

        <section className="runes-accordion-card card" id="perusolemus">
          <button
            type="button"
            className="runes-accordion-trigger"
            aria-expanded={openSection === 7}
            onClick={() => setOpenSection(openSection === 7 ? null : 7)}
          >
            <span className="runes-accordion-title">Riimujen perusolemus – symbolinen kartta</span>
          </button>
          {openSection === 7 && (
            <div className="runes-accordion-content">
              <p className="runes-essence-intro">
                Tässä osiossa jokaisen riimun perusolemus on kerrattuna: riimut muodostavat
                symbolisen järjestelmän, eivät erillisiä merkityksiä. Voit selata riimuja
                aakkosjärjestyksessä tai suodattaa aetin ja teeman mukaan.
              </p>

              <h3 className="runes-explain-heading">Mitä aetit ja teemat tarkoittavat?</h3>
              <h4 className="runes-explain-subheading">Aetit – kolme kahdeksikon oppipolkua</h4>
          <p>
            Vanha futhark jaetaan kolmeen kahdeksan riimun ryhmään eli aettiin (aett 1, 2, 3).
            Aetit eivät ole jäykät säännöt vaan oppimisen vaiheita: voit ajatella niitä kolmena
            polkuna, joilla jokainen riimu saa kontekstinsa.
          </p>
          <ul className="runes-course-steps runes-explain-list">
            <li><strong>Aett 1 (perusta):</strong> Ensimmäiset kahdeksan riimua – turvallisuus, voima, rajat, viesti, liike, valo, antaminen, ilo. Vaihe, jossa perustat yhteyden riimuihin ja tunnistat perusvoimat.</li>
            <li><strong>Aett 2 (haaste ja muutos):</strong> Keskimmäinen kahdeksikko – luonnonvoimat, tarve, pysähtyminen, syklit, tuki, salattu mahdollisuus, suoja, valo ja oikeus. Vaihe, jossa kohtaat haasteet ja muutoksen.</li>
            <li><strong>Aett 3 (integraatio ja selkeys):</strong> Viimeinen kahdeksan – oikeus, kasvu, yhteistyö, identiteetti, vuo, potentiaali, läpimurto, koti. Vaihe, jossa asiat yhdistyvät ja selkeytyvät.</li>
          </ul>

          <h4 className="runes-explain-subheading">Teemat – käytännön suodattimet tulkinnan tueksi</h4>
          <p>
            Teemat ovat tulkinnan apuvälineitä, ei ehtoja: ne auttavat kysymään „mikä tilanne tässä on?“ eivätkä määritä, mitä riimun „pitää“ tarkoittaa. Voit suodattaa riimut teeman mukaan ja valita yhden, joka resonoi tämän päivän kysymyksen kanssa.
          </p>
          <ul className="runes-explain-themes">
            <li><strong>Suoja</strong> – rajat, turvallisuus ja vaiston kuuntelu. <em>Esimerkkikysymys: Missä tarvitsen rajaa tai turvaa?</em></li>
            <li><strong>Muutos</strong> – liike, luonnonvoimat ja uuden alun mahdollisuus. <em>Esimerkkikysymys: Mitä haluaa muuttua – ja mitä voin hyväksyä?</em></li>
            <li><strong>Kasvu</strong> – kypsyminen, hoito ja potentiaali. <em>Esimerkkikysymys: Mitä minun pitäisi vaalia tai antaa kasvaa?</em></li>
            <li><strong>Rajat</strong> – selkeytys, kynnys ja torjunta. <em>Esimerkkikysymys: Mihin minun pitäisi sanoa „ei“?</em></li>
            <li><strong>Yhteys</strong> – yhteistyö, kuuleminen ja kuuluminen. <em>Esimerkkikysymys: Kenen tai minkä kanssa tunnet yhteyttä juuri nyt?</em></li>
            <li><strong>Selkeys</strong> – valo, oivallus ja tarkoitus. <em>Esimerkkikysymys: Mikä asia tarvitsee valoa tai rehellistä katsantaa?</em></li>
          </ul>

          <p className="runes-explain-together">
            <strong>Yhdessä:</strong> Teema antaa tilanteen tai kysymyksen („mikä aihe minua nyt liikuttaa?“), aett antaa vaiheen („olenko perustamassa, kohdassa haasteessa vai integroimassa?“). Voit valita ensin teeman ja sitten aetin – tai päinvastoin – ja selata hakemistosta riimut, jotka osuvat molempiin.
          </p>

          <p>
            Alla voit selata kaikki kurssin riimut ja käyttää suodattimia aetin, teeman tai haun mukaan.
          </p>
          <p className="runes-filter-helper">
            Teemat ovat tulkinnan linssinä. Sama riimu voi kuulua useampaan teemaan.
          </p>

          <span id="aakkosjarjestys" className="runes-anchor-offset" aria-hidden="true" />
          <div id="hakemisto">
          <div className="runes-course-filters" id="teemat" ref={teematRef}>
            <div className="runes-course-search">
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Hae nimen, ääntämyksen tai avainsanan mukaan..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="runes-search-input"
                aria-label="Hae riimuja"
              />
            </div>
            <div className="runes-course-filter-row">
              <label className="runes-filter-label">
                Aett
                <select
                  value={aettFilter}
                  onChange={(e) => setAettFilter(e.target.value)}
                  className="runes-filter-select"
                  aria-label="Suodata aetin mukaan"
                >
                  <option value="">Kaikki</option>
                  <option value="1">1</option>
                  <option value="2">2</option>
                  <option value="3">3</option>
                </select>
              </label>
              <label className="runes-filter-label">
                Teema
                <select
                  value={teemaFilter}
                  onChange={(e) => setTeemaFilter(e.target.value)}
                  className="runes-filter-select"
                  aria-label="Suodata teeman mukaan"
                >
                  <option value="">Kaikki</option>
                  {THEMES.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </label>
            </div>
          </div>

          <div className="runes-grid">
            {filtered.length === 0 ? (
              <p className="runes-course-empty">Ei tuloksia haulla tai suodattimilla.</p>
            ) : (
              filtered.map((rune) => (
                <Link
                  key={rune.slug}
                  to={`/courses/runes/${rune.slug}`}
                  className="rune-card card"
                >
                  <div className="rune-card-image-wrap rune-image-zoom">
                    <img
                      src={rune.image}
                      alt={rune.nameFi}
                      className="rune-card-image rune-image"
                    />
                  </div>
                  <h3 className="rune-card-name">{rune.nameFi}</h3>
                  <p className="rune-card-phonetic">{rune.phonetic}</p>
                  <div className="rune-card-keywords">
                    {rune.keywordsFi &&
                      rune.keywordsFi.slice(0, 3).map((k) => (
                        <span key={k} className="rune-keyword-tag">
                          {k}
                        </span>
                      ))}
                  </div>
                </Link>
              ))
            )}
          </div>
          </div>
              <p className="runes-back-to-top">
                <a href="#aloita" onClick={scrollToAloita}>↑ Takaisin Aloita tästä</a>
              </p>
            </div>
          )}
        </section>

        <section className="runes-accordion-card card" id="7-paivaa">
          <button
            type="button"
            className="runes-accordion-trigger"
            aria-expanded={openSection === 8}
            onClick={() => setOpenSection(openSection === 8 ? null : 8)}
          >
            <span className="runes-accordion-title">7 päivän riimuharjoitus – kuule se, minkä jo tiedät</span>
          </button>
          {openSection === 8 && (
            <div className="runes-accordion-content runes-7day-section">
              <p className="runes-7day-section-intro">
                Tämä harjoitus syventää riimutyöskentelyä. Suosittelemme tutustumaan ensin kohtaan 7: Riimujen perusolemus.
              </p>
              <p className="runes-7day-intro">
                Seitsemän päivää, yksi riimu päivässä. Pidä jokaiselle päivälle 5–10 minuuttia:
                älä analysoi vaan kuuntele. Riimu ei kerro tulevaisuutta – se peilaa sitä, mitä jo
                tiedät ja tarvitset kuulla. Anna itsellesi hiljaisuus ja yksi kysymys kerrallaan.
              </p>

              <div className="runes-7day-day card">
                <h3 className="runes-7day-day-title">Päivä 1 – Pysähtyminen</h3>
                <p><strong>Fokus:</strong> Pysähdy. Ei tavoitteita, vain yksi hengitys kerrallaan.</p>
                <p className="runes-7day-kysymys"><em>Mitä tapahtuu, kun en tee mitään muuta kuin hengitän?</em></p>
                <p><strong>Pieni rituaali (1 min):</strong></p>
                <ol className="runes-course-steps">
                  <li>Istu tai makaa liikkumatta. Sulje silmät.</li>
                  <li>Hengitä kolme kertaa hitaasti: sisään nenän kautta, ulos suun kautta.</li>
                  <li>Sano mielessäsi: „Tässä hetkessä ei tarvitse tehdä mitään.“</li>
                </ol>
                <p><strong>Päiväkirjaan:</strong> Mitä huomasit kehostasi tai mielestäsi kun pysähdyit? Yksi lause riittää.</p>
              </div>

              <div className="runes-7day-day card">
                <h3 className="runes-7day-day-title">Päivä 2 – Kuuleminen</h3>
                <p><strong>Fokus:</strong> Sisäinen ääni. Ei tarvitse vastata – vain kuunnella.</p>
                <p className="runes-7day-kysymys"><em>Minkä lauseen tarvitsen tänään kuulla?</em></p>
                <p><strong>Pieni rituaali (1 min):</strong></p>
                <ol className="runes-course-steps">
                  <li>Pysähdy ja hengitä rauhallisesti.</li>
                  <li>Kysy mielessäsi: Mitä minun pitäisi tänään kuulla?</li>
                  <li>Odota hetki. Kirjoita yksi lause ylös – ei tarvitse olla looginen.</li>
                </ol>
                <p><strong>Päiväkirjaan:</strong> Mikä lause tuli mieleen? Miltä sen sanominen (ääneen tai mielessä) tuntui?</p>
              </div>

              <div className="runes-7day-day card">
                <h3 className="runes-7day-day-title">Päivä 3 – Peilaus</h3>
                <p><strong>Fokus:</strong> Valitse yksi riimu kurssin hakemistosta. Lue vain sen ydinviesti (Ydin).</p>
                <p className="runes-7day-kysymys"><em>Mitä tämä riimu peilaa minun elämässäni juuri nyt?</em></p>
                <p><strong>Pieni rituaali (1 min):</strong></p>
                <ol className="runes-course-steps">
                  <li>Katso riimun kuvaa tai kuvaile sitä mielessäsi.</li>
                  <li>Toista ydinviesti hiljaa itsellesi.</li>
                  <li>Kysy: Missä näen tämän elämässäni tällä viikolla?</li>
                </ol>
                <p><strong>Päiväkirjaan:</strong> Mitä yksi asia – tilanne, tunne tai toive – tuli mieleen?</p>
              </div>

              <div className="runes-7day-day card">
                <h3 className="runes-7day-day-title">Päivä 4 – Ruumiintuntemus</h3>
                <p><strong>Fokus:</strong> Keho tietää. Kuuntele sitä ennen kuin analysoit.</p>
                <p className="runes-7day-kysymys"><em>Missä kehossani tai vartalossani tämä riimu / tämä viesti asuu?</em></p>
                <p><strong>Pieni rituaali (1 min):</strong></p>
                <ol className="runes-course-steps">
                  <li>Laita käsi kehon paikalle, johon huomio kiinnittyy (rinta, vatsa, otsa).</li>
                  <li>Hengitä sinne 2–3 kertaa.</li>
                  <li>Kysy keholta: Mitä sinä tarvitset tänään?</li>
                </ol>
                <p><strong>Päiväkirjaan:</strong> Mikä kehon paikka valitsit ja miksi? Mitä se „sanoi“?</p>
              </div>

              <div className="runes-7day-day card">
                <h3 className="runes-7day-day-title">Päivä 5 – Kysymys</h3>
                <p><strong>Fokus:</strong> Yksi kysymys kerrallaan. Älä vastaa – kuljeta sitä mukana.</p>
                <p className="runes-7day-kysymys"><em>Minkä kysymyksen kannan tänään mukana ilman vastausta?</em></p>
                <p><strong>Pieni rituaali (1 min):</strong></p>
                <ol className="runes-course-steps">
                  <li>Valitse yksi kysymys (voit palata eilisen riimun tai päiväkirjan aiheeseen).</li>
                  <li>Sano se ääneen tai kirjoita se ylös.</li>
                  <li>Anna sen olla. Ei tarvitse ratkaista sitä tänään.</li>
                </ol>
                <p><strong>Päiväkirjaan:</strong> Mikä kysymys sinulla on? Miltä tuntuu kantaa sitä mukana?</p>
              </div>

              <div className="runes-7day-day card">
                <h3 className="runes-7day-day-title">Päivä 6 – Yksi riimu</h3>
                <p><strong>Fokus:</strong> Valitse taas yksi riimu – sama tai uusi. Anna sen olla „kumppanisi“ tänään.</p>
                <p className="runes-7day-kysymys"><em>Mitä tämä riimu muistuttaa minua tänään?</em></p>
                <p><strong>Pieni rituaali (1 min):</strong></p>
                <ol className="runes-course-steps">
                  <li>Lue riimun „Tulkinta-apu“ -kohta kurssisivulta.</li>
                  <li>Valitse yksi pieni teko, jonka riimu ehdottaa – tee se.</li>
                  <li>Kiitä mielessäsi itsesi tai riimua.</li>
                </ol>
                <p><strong>Päiväkirjaan:</strong> Minkä teon teit? Miltä se tuntui?</p>
              </div>

              <div className="runes-7day-day card">
                <h3 className="runes-7day-day-title">Päivä 7 – Yhteenveto</h3>
                <p><strong>Fokus:</strong> Sulkeutuminen. Mitä kuulit? Ei arvostelua – vain tunnustus.</p>
                <p className="runes-7day-kysymys"><em>Mitä yksi asia nämä seitsemän päivää opettivat tai muistuttivat minulle?</em></p>
                <p><strong>Pieni rituaali (1 min):</strong></p>
                <ol className="runes-course-steps">
                  <li>Pysähdy ja hengitä kolme kertaa hitaasti.</li>
                  <li>Sano mielessäsi: „Kuulin jotain. Kiitän itseäni.“</li>
                  <li>Kirjoita yksi lause päiväkirjaan – se on riittävä yhteenveto.</li>
                </ol>
                <p><strong>Päiväkirjaan:</strong> Mitä yhden lauseen yhteenveto sinulle on? Haluatko jatkaa 24 päivän syventävällä polulla?</p>
              </div>

              <p className="runes-7day-outro">
                <strong>Seuraava askel:</strong>{' '}
                <a href="#24-paivaa" onClick={openAndScrollTo24}>24 päivän syventävä riimupolku</a>
                {' '}– yksi riimu per päivä koko vanhasta futharkista.
                Löydät ohjeet avatusta osiosta ja hakemistosta voit valita seuraavan riimun.
              </p>
              <p className="runes-back-to-top">
                <a href="#aloita" onClick={scrollToAloita}>↑ Takaisin Aloita tästä</a>
              </p>
            </div>
          )}
        </section>

        <section className="runes-accordion-card card">
          <button
            id="24-paivaa"
            type="button"
            className="runes-accordion-trigger"
            aria-expanded={openSection === 9}
            onClick={() => setOpenSection(openSection === 9 ? null : 9)}
          >
            <span className="runes-accordion-title">24 päivän syventävä riimupolku</span>
          </button>
          {openSection === 9 && (
            <div className="runes-accordion-content">
              <p className="runes-24day-intro">
                Tämä polku ei ole haaste vaan kutsu. Kahdenkymmenen neljän päivän ajan kuljet koko vanhan
                futharkin läpi – yksi riimu kerrallaan, hitaasti. Ei kiirettä, ei tavoitetta „suorittaa“:
                jokainen päivä on tilaa kuunnella, mitä yksi riimu sinulle tänään peilaa. Riimut eivät
                ennusta; ne ovat peili. Polku on rituaali, ei ohjelma.
              </p>

              <h3 className="runes-explain-heading">Yksi riimu per päivä – kuuleminen</h3>
              <p>
                Jokainen päivä on yhden riimun päivä. Et opiskele listaa vaan otat vastaan yhden viestin,
                yhden kysymyksen, yhden pienen rituaalin. Käytäntö on kuulemista: pysähdyt, avaat kurssin
                hakemiston (kohta 7), valitset päivän riimun ja annat sen olla kanssasi. Ei tarvitse
                ratkaista mitään – riittää, että kuulit.
              </p>

              <h3 className="runes-explain-heading">Kolme vaihetta – Herääminen, Koettelemus, Paluu</h3>
              <p>
                Vanha futhark jakautuu kolmeen kahdeksan riimun ryhmään (aett). Voit kokea ne kolmena
                polun vaiheena: herääminen perusvoimiin, koettelemus ja muutos, paluu ja selkeys.
              </p>

              <h4 className="runes-explain-subheading">Herääminen (päivät 1–8)</h4>
              <p>
                Ensimmäiset kahdeksan: {runes.slice(0, 8).map((r) => r.nameFi).join(', ')}.
                Vaihe, jossa perustat yhteyden – turvallisuus, voima, rajat, viesti, liike, valo,
                antaminen, ilo. Riimut kutsuvat heräämään siihen, mitä jo on.
              </p>

              <h4 className="runes-explain-subheading">Koettelemus (päivät 9–16)</h4>
              <p>
                Keskimmäinen kahdeksikko: {runes.slice(8, 16).map((r) => r.nameFi).join(', ')}.
                Vaihe, jossa kohtaat luonnonvoimat, tarpeen, pysähtymisen, syklit, tuen ja suojan.
                Ei vaikeutta vaan syvyyttä – mitä vaatii hyväksymistä tai muutosta?
              </p>

              <h4 className="runes-explain-subheading">Paluu (päivät 17–24)</h4>
              <p>
                Viimeinen kahdeksan: {runes.slice(16, 24).map((r) => r.nameFi).join(', ')}.
                Vaihe, jossa asiat yhdistyvät – oikeus, kasvu, yhteistyö, identiteetti, vuo,
                potentiaali, läpimurto, koti. Paluu ei ole loppu vaan uusi alku selkeyden kanssa.
              </p>

              <h3 className="runes-explain-heading">Päivän rituaali</h3>
              <p>
                Voit antaa jokaiselle päivälle kevyen rakenteen. Ei pakkoa – vain tapa kunnioittaa
                yhden riimun aikaa.
              </p>
              <ul className="runes-24day-structure">
                <li><strong>Päivän riimu</strong> – Valitse seuraava riimu aakkosjärjestyksessä (Fehu → … → Othala) tai aetin mukaan. Avaa sen sivu hakemistosta (kohta 7).</li>
                <li><strong>Kuuleminen</strong> – Lue riimun Ydin. Mitä tämä riimu sinulle tänään peilaa? Ei vastausta – vain kysymys kuljettavana mukana.</li>
                <li><strong>Kysymys</strong> – Yksi kysymys riimun tai oman tilanteen pohjalta. Riimun sivulta tai oma. Anna sen olla.</li>
                <li><strong>Pieni rituaali (1–2 min)</strong> – Hengitä. Lue riimun „Päivän pieni rituaali“ -kohta tai tee oma lyhyt pysähdys: yksi lause, yksi hengitys, yksi kiitos.</li>
                <li><strong>Päiväkirjaan</strong> – Yksi lyhyt merkintä: mitä kuulit, mitä kysymys herätti. Ei analyysiä – vain tunnustus.</li>
              </ul>

              <p className="runes-24day-reassurance">
                Voit ohittaa päiviä. Polku on armollinen: jos päivä jää väliin, jatka seuraavasta.
                Rytmi on apu, ei velvoite. Tärkeintä on kuuleminen, ei suoritus.
              </p>

              <p className="runes-back-to-top">
                <a href="#aloita" onClick={scrollToAloita}>↑ Takaisin Aloita tästä</a>
              </p>
            </div>
          )}
        </section>

        <section className="runes-course-block runes-course-sources card">
          <h2>Lähteet</h2>
          <p className="runes-course-sources-intro">
            Kurssin tausta nojautuu muun muassa seuraaviin lähteisiin. Suosittelemme lukemaan
            lisää riimukirjoituksesta, historiasta ja symboliikasta.
          </p>
          <ul className="runes-course-sources-list">
            {WIKIPEDIA_SOURCES.map((s) => (
              <li key={s.url}>
                <a href={s.url} target="_blank" rel="noopener noreferrer" className="runes-course-source-link">
                  {s.title}
                </a>
              </li>
            ))}
            {COURSE_SOURCES.map((s) => (
              <li key={s.url}>
                <a href={s.url} target="_blank" rel="noopener noreferrer" className="runes-course-source-link">
                  {s.title}
                </a>
              </li>
            ))}
          </ul>
        </section>
      </div>
    </div>
  );
};

export default RunesCourseIndex;
